import React, { useEffect, useState, useCallback } from 'react';
import { Card, Select, Btn, Table, Badge, RNLLoader, Modal } from '../UI';
import { fmt, fmtDate } from '../../utils';
import * as api from '../../api';
import { pvStatusColor, pvStatusLabel, reviewStatusColor, STATUS_BADGE_STYLE } from './pvUtils';
import EyeButton from './EyeButton';
import PrintButton from './PrintButton';
import DownloadPDFButton from './DownloadPDFButton';
import VoucherDetailView from './VoucherDetailView';
import VoucherResubmission from './VoucherResubmission';
import Pagination from '../requisition/Pagination';


const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'approved', label: 'Approved' },
  { value: 'posted', label: 'Posted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'returned', label: 'Returned' },
  { value: 'cancelled', label: 'Cancelled' },
];

export default function MyVouchers({ onEditDraft, onOpenNewVoucher, toast, openVoucherId, onOpenVoucher }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState({ skip: 0, limit: 10 });
  const [totalCount, setTotalCount] = useState(0);
  // Which voucher is open. Controlled when the caller supplies openVoucherId,
  // uncontrolled otherwise — aliased to the same names so every reference in
  // this file is unchanged either way.
  const [viewingIdState, setViewingIdState] = useState(null);
  const viewingId = openVoucherId !== undefined ? openVoucherId : viewingIdState;
  const setViewingId = onOpenVoucher || setViewingIdState;
  const [resubmittingId, setResubmittingId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  // Separate from busyId: Edit and Delete sit on the same row, and one shared
  // flag made both spin whichever was clicked.
  const [editingId, setEditingId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'delete', row }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listPaymentVouchers({ mine_only: true, status: statusFilter || undefined, skip: page.skip, limit: page.limit });
      setRows(res?.vouchers || []);
      setTotalCount(res?.total_count ?? 0);
    } catch (e) {
      toast.show(e.message, 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, page]);

  useEffect(() => { load(); }, [load]);

  const handleEdit = async (row) => {
    // The full voucher has to be fetched before the form can open, and that
    // round-trip was silent — the button looked dead for a second or two.
    setEditingId(row.payment_voucher_id);
    try {
      const full = await api.getPaymentVoucher(row.payment_voucher_id);
      onEditDraft(full);
    } catch (e) {
      toast.show(e.message, 'error');
    } finally {
      setEditingId(null);
    }
  };

  const handleDelete = (row) => setConfirmAction({ type: 'delete', row });

  const runConfirmedAction = async () => {
    // Delete is the only confirmable action left here. Posting used to live
    // on this screen; submitting a voucher now posts it automatically, and
    // releasing belongs to Voucher Review & Release.
    const { row } = confirmAction;
    setConfirmAction(null);
    setBusyId(row.payment_voucher_id);
    try {
      await api.deletePaymentVoucher(row.payment_voucher_id);
      toast.show('Draft deleted.', 'success');
      load();
    } catch (e) {
      toast.show(e.message, 'error');
    } finally {
      setBusyId(null);
    }
  };

  if (resubmittingId) {
    return (
      <VoucherResubmission
        voucherId={resubmittingId}
        toast={toast}
        onBack={() => setResubmittingId(null)}
        onDone={() => { setResubmittingId(null); load(); }}
      />
    );
  }

  if (viewingId) {
    return (
      <VoucherDetailView
        voucherId={viewingId}
        onBack={() => { setViewingId(null); load(); }}
        toast={toast}
        showWorkflowActions={false}
        onActed={load}
        onResubmit={(full) => { setViewingId(null); setResubmittingId(full.payment_voucher_id); }}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ width: 220 }}>
          <Select
            value={statusFilter}
            onChange={(v) => { setStatusFilter(v); setPage(p => ({ ...p, skip: 0 })); }}
            options={STATUS_FILTER_OPTIONS}
          />
        </div>
        <Btn onClick={onOpenNewVoucher}>New Payment Voucher</Btn>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
      <Card style={{ border: '1px solid black', borderBottom: !loading ? 'none' : '1px solid black', borderRadius: 0, boxShadow: 'none', overflow: 'hidden', padding: 0 }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><RNLLoader title="Loading your vouchers…" /></div>
        ) : (
          <>
          <Table
            cols={[
              { key: 'voucher_no', label: 'VOUCHER NO.', render: v => v || <span style={{ color: 'var(--text-muted)' }}>Not yet submitted</span> },
              { key: 'voucher_date', label: 'DATE', render: v => fmtDate(v) },
              { key: 'supplier_name', label: 'SUPPLIER', render: v => v || '—' },
              { key: 'purchase_request_pr_number', label: 'REQUISITION', render: v => v || '—' },
              // Once a voucher is finished it is on nobody's desk — "Not
              // Assigned" implied it was still waiting for someone.
              { key: 'currently_with', label: 'CURRENTLY WITH', align: 'center', render: v => v || <span style={{ color: 'var(--text-muted)' }}>—</span> },
              { key: 'net_payable', label: 'NET PAYABLE', align: 'right', render: v => <span style={{ fontWeight: 700 }}>{fmt(v)}</span> },
              {
                key: 'status', label: 'STATUS', align: 'center',
                render: (v, row) => (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <Badge
                      color={row.review_status ? reviewStatusColor(row.review_status) : pvStatusColor(v)}
                      pulse={row.review_status === 'Awaiting Review'}
                      style={STATUS_BADGE_STYLE}
                    >
                      {row.review_status || pvStatusLabel(v)}
                    </Badge>
                    {/* The reason a voucher came back belongs with the status
                        that says it came back — the owner should not have to
                        open it to find out what to correct. */}
                    {row.return_reason && (
                      <div style={{
                        fontSize: 11, fontStyle: 'italic', color: '#c2410c',
                        maxWidth: 220, whiteSpace: 'normal', lineHeight: 1.4,
                      }}>“{row.return_reason}”</div>
                    )}
                  </div>
                ),
              },
              {
                key: 'payment_voucher_id', label: 'ACTIONS', align: 'center', render: (_, row) => (
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
                    {row.status === 'draft' && (
                      <>
                        <Btn size="sm" variant="ghost" loading={editingId === row.payment_voucher_id} onClick={() => handleEdit(row)}>Edit</Btn>
                        <Btn size="sm" variant="danger" loading={busyId === row.payment_voucher_id} onClick={() => handleDelete(row)}>Delete</Btn>
                      </>
                    )}
                    {/* A returned voucher is corrected and resubmitted, never
                        saved back to draft — so this goes straight to the
                        Correct & Resubmit screen rather than the plain form,
                        whose Save Draft / Submit footer does not apply here. */}
                    {row.status === 'returned' && (
                      <Btn size="sm" variant="ghost" onClick={() => setResubmittingId(row.payment_voucher_id)}>Correct</Btn>
                    )}
                    {row.status !== 'draft' && (
                      <>
                        <EyeButton onClick={() => setViewingId(row.payment_voucher_id)} />
                        <PrintButton voucherId={row.payment_voucher_id} toast={toast} />
                        <DownloadPDFButton voucherId={row.payment_voucher_id} voucherNo={row.voucher_no} toast={toast} />
                      </>
                    )}
                  </div>
                )
              },
            ]}
            rows={rows}
            emptyMsg="You haven't created any payment vouchers yet."
          />
          </>
        )}
      </Card>
      {!loading && (
        <div style={{ borderTop: '1px solid black', borderLeft: '2.5px solid black', borderRight: '2.5px solid black', borderBottom: '2.5px solid black', background: 'var(--bg-card)' }}>
          <Pagination total={totalCount} skip={page.skip} limit={page.limit} onChange={setPage} />
        </div>
      )}
      </div>

      {confirmAction?.type === 'delete' && (
        <Modal title="Delete Draft" onClose={() => setConfirmAction(null)} width={440}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>
              Delete draft <strong>{confirmAction.row.voucher_no || '(unsubmitted)'}</strong>? This cannot be undone.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <Btn variant="ghost" onClick={() => setConfirmAction(null)}>Cancel</Btn>
              <Btn variant="danger" onClick={runConfirmedAction}>Delete</Btn>
            </div>
          </div>
        </Modal>
      )}

    </div>
  );
}
